import React from 'react';
import { TCN, TCN_COLOR_MAP } from './tokens';

/**
 * TCN Sparkline — minimal inline polyline (no chart lib).
 *
 * Props:
 *  - data: number[] (default [])
 *  - width, height: defaults 60×22
 *  - color: semantic key or CSS color (default 'cyan')
 *  - filled: boolean — fills under the line. Default false per mockup ui-kit.jsx:440 (polyline only).
 *  - strokeWidth: default 1.5
 */
const Sparkline = ({
  data = [],
  width = 60,
  height = 22,
  color = 'cyan',
  filled = false,
  strokeWidth = 1.5,
  className = '',
  style,
}) => {
  const stroke = TCN_COLOR_MAP[color] ?? color;

  if (!data || data.length === 0) {
    return (
      <svg width={width} height={height} className={className} style={style} aria-hidden="true">
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={TCN.textMute}
          strokeWidth={1}
          strokeDasharray="2 3"
          opacity={0.5}
        />
      </svg>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length === 1 ? 0 : width / (data.length - 1);

  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return [x, y];
  });

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
  const fillPath = `${linePath} L${width},${height} L0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      className={className}
      style={style}
      role="img"
      aria-label="Sparkline trend"
    >
      {filled && <path d={fillPath} fill={stroke} opacity={0.18} />}
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default Sparkline;
